import { Helmet } from "react-helmet";
import BodyText from "components/bodytext";
import ContentBox from "components/contentbox";
import HeaderText from "components/headertext";
import Image from "components/image";
import husbandryImg1 from "img/husbandry/Dumerils Boas 1.png";
import husbandryImg2 from "img/husbandry/Dumerils Boas 2.png";
import husbandryImg3 from "img/husbandry/Dumerils Boas 3.png";
import husbandryImg4 from "img/husbandry/Dumerils Boas 4.png";
import husbandryImg5 from "img/husbandry/Dumerils Boas 5.png";
import husbandryImg6 from "img/husbandry/Dumerils Boas 6.png";
import husbandryImg7 from "img/husbandry/Dumerils Boas 7.png";
import husbandryImg8 from "img/husbandry/Dumerils Boas 8.png";
import husbandryImg9 from "img/husbandry/Dumerils Boas 9.png";
import husbandryImg10 from "img/husbandry/Dumerils Boas 10.png";

const HusbandryCharts = () => {
  return (
    <ContentBox>
      <Helmet>
        <title>How to Read the Husbandry Charts</title>
        <meta
          name="description"
          content="How to read the husbandry charts"
        />
        <link rel="canonical" href="https://www.gcreptiles.com/husbandry-charts" />
      </Helmet>
      <HeaderText
        title='How to Read the Husbandry Charts'
      />
  
      <BodyText>
        <p>
          I have a chart for each species because obviously each can be different. 
          But there are many similarities.
        </p>
        <Image src={husbandryImg1} />
        <p>
          For each species, there are 6 elements of their 
          care that I track throughout the year. These elements are shown on the left side
          of each chart. They are:
        </p>
        <ol>
          <li>Their daytime high temperature</li>
          <li>Their nighttime low temperature</li>
          <li>Whether they need the humdity increased above normal levels in the room</li>
          <li>Light cycling</li>
          <li>Feeding, split by males and females</li>
          <li>When to pair males and females for breeding</li>
        </ol>

        <h2>Temperatures</h2>
        <p>
          At the top of the charts are the temperature settings.
        </p>
        <Image src={husbandryImg2} />
        <p>
          Most of my animals are in cages so I’m going to describe the things I do with the cages.
        </p>
        <p>
          Each cage has a radiant heat panel in the ceiling on one side of the cage. Having it on the side helps 
          provide a warm-to-cool temperature gradient within each enclosure. This is important.
        </p>

        <h3>Basking Spots</h3>
        <p>
          For each cage, I plan on one or more basking spots for the snake. The basking spot is the place,
          where I expect the snake to go when it needs some additional heat, usually during the first couple 
          of days after it eats, or for females to use when they are gravid (pregnant). The basking spot might 
          be on the ground, or up in the branches.
        </p>
        
        <h3>Daytime High (or DTH)</h3>
        <Image src={husbandryImg3} />
        <p>
          The “daytime high” temperature is the highest the temperature gets during the day at 
          the basking spot. So for example, if I say the daytime high for a boa is “85F (12h)”, then 
          the temperature will 
          measure 85F at the basking spot under the heat panel for about 12 hours each day. 
          However, the cooler side of the cage will be 4-7 degrees cooler. This way the snake 
          can choose what temperature it wants to be at.
        </p>
        <p>
          In the morning, the set point of the thermostat is set to a temperature that will get the basking 
          spot up to the desired daytime high. The thermostat turns on the heat panel and the temperature rises 
          pretty quickly.
        </p> 
        <p>
          But setting the thermostat’s temperature to the value I want doesn’t always mean the basking spot 
          will go to exactly that temperature. So I put the thermostat’s probe as close as I can to where I 
          want the animal to be when it basks, but I also use a temperature gun and/or a second hygrometer to 
          measure the temperature at the spot. I use the temp gun or the hygrometer to tell me more accurately 
          what the temperature is, and then I adjust the thermostat accordingly.
        </p>

        <h3>Nighttime Low (or NTL)</h3>
        <Image src={husbandryImg4} />
        <p>
          At the end of the daytime heating cycle the setpoint of the thermostat is changed and the cage will 
          gradually drop in temperature to the “nighttime low”. It may take only an hour or two to drop, or it 
          may take most of the night. I don’t think the rate of decrease matters. But the setpoint on the 
          thermostat is adjusted to keep the temperature from dropping further.
        </p>

        <h2>Temperature Cycling</h2>
        <Image src={husbandryImg5} />
        <p>
          Most animals get a seasonal change at some point during the year. This is called temperature cycling. 
          Most reptiles in the wild that don’t live on the equator have a natural temperature cycle during the year. 
          Many keepers mimic this temperature cycle for snakes in captivity, primarily for breeding but it can also 
          be done to maintain the natural cycles the animal would experience in the wild.
        </p>
        <p>
          For temperature cycling, I bring the daytime high and nighttime low temperatures down for several months. 
          At the beginning, the temperatures are dropped gradually for about a month, then the new temperatures are 
          maintained for a period of time, and then the temperatures are gradually raised to their new target temperatures 
          over the span of about a month. To do these gradual decreases and increases I use a combination of thermostat 
          setpoint changes and I will also move snakes to different rooms in the house that are cooler or warmer.
        </p>

        <h2>Humidity</h2>
        <Image src={husbandryImg6} />
        <p>
          Humidity is pretty straight-forward. I keep my animals on coconut bark substrate and I make sure that the base
          layer is damp so that the humidity in the cage is in the range that I want. Generally, my room throughout the
          year is at about 40-45% relative humidity, and I adjust the humidity for tropical species so that their humidity
          is about 60-80%. More temperate species have their humidity adjusted to be in the 50-60% range.
        </p>
        <p>
          When talking about humidity I want to point out that each cage has multiple vents in it to allow for air circulation,
          and there are 6 floor fans in the room pushing air in different directions. So I am able to achieve humid conditions
          in the cages without the air being stale and the substrate developing mold. It's a balancing act and one that took
          me several years to nail down.
        </p>


        <h2>Lighting</h2>
        <Image src={husbandryImg7} />
        <p>
          Lighting is also straight-forward. All of my cages have LED lights that are controlled by Wi-Fi programmable switches.
          The programming on the switches lets me add or reduce hours in the day for the LEDs to be on.
        </p>
        <p>
          There is also a window in the room, but it only lets in a little bit of light.
        </p>


        <h2>Feeding</h2>
        <Image src={husbandryImg8} />
        <p>
          For feeding notice that there is a pink bar that I use for female snakes, and a blue bar for the males. They are 
          pretty simple to understand though. Most of my adult animals get fed once or twice a month for about 6 months of the year.
          Then there is a period of about 2 months where they tend to get more food than usual. I also make the horizontal
          bars a darker shade so that I don't miss this when I am reviewing the charts. This extra feeding frequently happens right
          before males and females are paired together. And then for the more temperate species there is a period of several
          months where they are not fed at all so that they can brumate.
        </p>
        <p>
          Be aware that the bars start and stop at particular places during the month. I don't know for sure that it matters,
          but it's the practice I follow. You might notice for example that a chart will show the pink bar for females of
          the species beginning earlier in the month than the blue bar for the males of the species. This is deliberate.
        </p>


        <h2>Pairing</h2>
        <Image src={husbandryImg9} />


        <h2>How I use these during the year</h2>
        <p>
          My standard practice is to go through all of the species charts at the beginning of each month. And I look 
          for the things that need to change during the new month.
        </p>
        <p>
          I start by checking all of the temperature readings from the hygrometers around the room and compare them 
          with what the expected temperatures need to be. Then I make start adjusting temperatures for any animals that need it.
        </p>
        <p>
          I do the same for humidity.
        </p>
        <p>
          For lighting, I start adjusting the timers that control the lights to lengthen or shorten the daylight period as necessary.
        </p>
        <p>
          I have a year-round feeding chart that has a row for each of my snakes and 52 columns for each week of the year. 
          I have that chart synced with the species charts. So the feeding adjustments are already dialed in.
        </p>
        <p>
          Finally I look to see if the pairing of males and females is changing during the new month. Since I don’t feed animals 
          when they are in the same cage together, if there is pairing happening and feeding happening for one or both animals in 
          a cage, I make plans for separating them to feed.
        </p>
        <p>
          I put all of the changes that have to be made for the month on my whiteboard so that I can track them during the month.
        </p>
        <Image src={husbandryImg10} />
        <p>
          For example, in this chart above, if it were the beginning of November, I would start decreasing the daytime high temperature
          of my Dumeril's boas, decreasing their nighttime low temperature, decreasing the number of hours of light they are getting, 
          only feeding females during the month, and the females would only get an occasional
          small meal maybe once or twice during the month. I would also pair male and female Dumeril's boas together.
        </p>
      </BodyText>
    </ContentBox>
  );
};

export default HusbandryCharts;
